import { type ClassValue, clsx } from "clsx";
import type { Metadata } from "next";
import { twMerge } from "tailwind-merge";
import type { JobSearchResultV2 } from "./actions/jobs";

export const mainMetaData: Metadata = {
  title: {
    default:
      "MindMyJob - The Hiring System is Automated. Your Search Should Be Too",
    template: "%s | MindMyJob - AI Job Search Platform",
  },
  description:
    "Auto Apply to 100+ AI Matched Jobs with MindMyJob's Auto Apply Agents. Use Smart Resume Builder to create ATS optimised Resumes for each job application with 90+ ATS Score. Access 50,000+ live job openings across 150+ cities from 2,500+ top companies. Leverage Insider Connections, Job Deep Research, Jobfit Scorer, AI Mock Interviews, and auto apply to jobs in Bulk to get 8x more interview calls every Week.",
  keywords: [
    "ai job search",
    "job search platform",
    "auto apply jobs",
    "resume builder ai",
    "interview preparation",
    "ai mock interviews",
    "job application automation",
    "bulk apply jobs",
    "career development",
    "job portal india",
    "ai resume maker",
    "ai job tracker",
    "career guidance",
    "employment platform",
    "job matching ai",
    "professional networking",
    "career advancement",
    "job search automation",
    "tech jobs india",
    "remote jobs india",
    "startup jobs india",
    "mnc careers india",
  ],
  authors: [{ name: "MindMyJob Team" }],
  creator: "MindMyJob",
  publisher: "MindMyJob",
  category: "Technology",
  classification: "AI Job Search Platform",
  metadataBase: new URL("https://www.mindmyjob.com"),
  icons: {
    icon: [
      { url: "/favicon-96x96.png", sizes: "96x96", type: "image/png" },
      { url: "/favicon.svg", type: "image/svg+xml" },
      { url: "/favicon.ico", rel: "shortcut icon" },
    ],
    apple: [{ url: "/apple-touch-icon.png", sizes: "180x180" }],
  },
  applicationName: "MindMyJob",
  manifest: "/site.webmanifest",
  appleWebApp: {
    capable: true,
    statusBarStyle: "default",
    title: "MindMyJob",
    startupImage: ["/apple-touch-icon.png"],
  },
  formatDetection: {
    telephone: false,
    email: false,
    address: false,
  },
  openGraph: {
    type: "website",
    siteName: "MindMyJob",
    title: {
      default:
        "MindMyJob - AI-Powered Job Search Platform | Auto Apply to 50,000+ Active Jobs",
      template: "%s | MindMyJob - AI Job Search Platform",
    },
    description:
      "MindMyJob - Auto Apply to 50,000+ Active Jobs with ATS Optimised resumes on MindMyJob. Includes Insider Connections, Smart Resume Builder, AI Mock Interview, AI Job Tracker, Cover Letter Copilot and more. Get 8x more interview calls.",
    url: "https://www.mindmyjob.com",
    locale: "en_US",
    images: [
      {
        url: "https://mmj-public-files.t3.storage.dev/admin-files/og_banner.png",
        width: 2048,
        height: 1264,
        alt: "MindMyJob Platform Preview",
        type: "image/png",
      },
    ],
  },
  twitter: {
    card: "summary_large_image",
    site: "@mindmyjob",
    creator: "@mindmyjob",
    title: {
      default: "MindMyJob - AI-Powered Job Search Platform | 50,000+ Jobs",
      template: "%s | MindMyJob - AI Job Search Platform",
    },
    description:
      "MindMyJob – Auto-apply with a tailored resume on MindMyJob. Includes AI Mock Interview, Smart Resume Builder and Insider Connections. Get 8x more interview calls.",
    images: [
      "https://mmj-public-files.t3.storage.dev/admin-files/og_banner.png",
    ],
  },
  robots: {
    index: true,
    follow: true,
    nocache: false,
    googleBot: {
      index: true,
      follow: true,
      noimageindex: false,
      "max-video-preview": -1,
      "max-image-preview": "large",
      "max-snippet": -1,
    },
  },
  alternates: {
    canonical: "https://www.mindmyjob.com",
    languages: {
      "en-US": "https://www.mindmyjob.com",
      "en-IN": "https://www.mindmyjob.com",
    },
  },
  verification: {
    google: "your-google-verification-code",
    yandex: "your-yandex-verification-code",
    yahoo: "your-yahoo-verification-code",
    other: {
      "msvalidate.01": "your-bing-verification-code",
    },
  },
  other: {
    "theme-color": "#ffffff",
    "color-scheme": "light",
    "mobile-web-app-capable": "yes",
    "apple-mobile-web-app-capable": "yes",
    "apple-mobile-web-app-status-bar-style": "default",
  },
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Converts a string to sentence case (first letter capitalized, rest lowercase)
 * @param {string} str - The string to convert
 * @returns {string} The sentence-cased string
 * @throws {Error} If input is empty, null, or undefined
 */
export function toSentenceCase(str: string): string {
  // Return the original string for invalid inputs
  if (str === undefined || str === null || str.trim().length === 0) {
    return str;
  }

  // Capitalize the first character of each word in the string
  return str
    ?.split(" ")
    ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    ?.join(" ");
}

/**
 * Generates a Supabase storage URL for a file
 * @param {string} filename - The name of the file
 * @param {string} folder - The folder path (default: 'public')
 * @param {string} bucket - The storage bucket name (default: 'admin-files')
 * @returns {string} The complete Supabase storage URL
 */
export function getSupabaseStorageUrl(
  filename: string,
  folder = "public",
  bucket = "admin-files",
): string {
  if (!filename) return filename;
  if (filename.startsWith("/") || /^https?:\/\//.test(filename)) {
    return filename;
  }
  return `https://vlolsgdgrjaamokcoyhn.supabase.co/storage/v1/object/public/${bucket}/${folder}/${filename}`;
}

/**
 * Converts a snake_case string (e.g., "HELLO_WORLD") to a readable sentence case format
 * @param {string} str - The snake_case string to convert
 * @returns {string} The formatted string in sentence case (e.g., "Hello World")
 */
export function formatSnakeCase(str: string | null | undefined): string {
  // Return empty string for invalid inputs
  if (str === undefined || str === null || str.trim().length === 0) {
    return "";
  }

  try {
    // Split by underscore, convert to lowercase, then capitalize first letter of each word
    return str
      .split("_")
      .map((word) => word.toLowerCase())
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  } catch (error) {
    console.error("Error formatting snake case string:", error);
    return str; // Return original string if any error occurs
  }
}

/**
 * Truncates a string to a specified maximum length, adding ellipsis if truncated
 * @param {string} text - The text to truncate
 * @param {number} maxLength - The maximum length of the truncated string
 * @returns {string} The truncated string with ellipsis if necessary
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength) + "...";
}

export const getTrimmedJobTitle = (title?: string | null) => {
  const safeTitle = title?.trim() ?? "";
  if (!safeTitle) return "";

  const specialCharIndex = safeTitle.search(/[-(){},"']/);
  if (specialCharIndex === -1) return truncateText(safeTitle, 35);

  const trimmedTitle = safeTitle.slice(0, specialCharIndex).trim();
  const displayTitle = trimmedTitle.length < 5 ? safeTitle : trimmedTitle;
  return truncateText(displayTitle, 35);
};

// Function to determine if the screenwidth is xs, sm, md, lg, xl, 2xl according to tailwind standards, with ts support for possible screen types
export type Breakpoint = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

/**
 * Determines the current Tailwind CSS breakpoint based on the window's inner width.
 * Falls back to "xs" if executed in a non-browser environment (e.g., server-side).
 * @returns {Breakpoint} The current breakpoint: "xs" | "sm" | "md" | "lg" | "xl" | "2xl"
 */
export function getBreakpoint(): Breakpoint {
  if (typeof window === "undefined") return "xs";

  const width = window.innerWidth;

  if (width >= 1536) return "2xl";
  if (width >= 1280) return "xl";
  if (width >= 1024) return "lg";
  if (width >= 768) return "md";
  if (width >= 640) return "sm";
  return "xs";
}

/**
 * Parses HTML and replaces multiple consecutive <br> tags with a single <br> tag using DOM traversing.
 * Ignores whitespace between consecutive break tags.
 * Will run only on the client.
 */
export const collapseMultipleBreaks = (html: string): string => {
  if (typeof window === "undefined" || !html) return html;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const brElements = Array.from(doc.querySelectorAll("br"));

    for (let i = 0; i < brElements.length; i++) {
      const currentBr = brElements[i];
      if (!currentBr.parentNode) continue;

      let nextNode = currentBr.nextSibling;
      while (nextNode) {
        if (
          nextNode.nodeType === Node.TEXT_NODE &&
          !nextNode.textContent?.trim()
        ) {
          const emptyTextNode = nextNode;
          nextNode = nextNode.nextSibling;
          emptyTextNode.parentNode?.removeChild(emptyTextNode);
          continue;
        }

        if (nextNode.nodeName.toLowerCase() === "br") {
          const extraBr = nextNode;
          nextNode = nextNode.nextSibling;
          extraBr.parentNode?.removeChild(extraBr);
          continue;
        }

        break;
      }
    }

    return doc.body.innerHTML;
  } catch (error) {
    console.error("Error collapsing break tags:", error);
    return html;
  }
};

export interface HeroFeature {
  id: string;
  name: string;
  title: string;
  description: string;
  imageSrc?: string;
  primaryActionText?: string;
  primaryActionLink?: string;
  secondaryActionText?: string;
  secondaryActionLink?: string;
}

// ---------------------------------------------------------------------------
// 2) Replace the existing heroFeatures array with this one.
//    Images point to /public/assets/images/features/.
// ---------------------------------------------------------------------------
export const heroFeatures: HeroFeature[] = [
  {
    id: "autoapply",
    name: "Auto Apply Agents",
    title: "Auto Apply Agents",
    description:
      "Three agents that find matching roles and apply for you every day, each with a resume tailored to the job.",
    primaryActionText: "Start Auto Applying",
    secondaryActionText: "See How It Works",
    primaryActionLink: "/autoapply",
    secondaryActionLink: "/ai-agents",
    imageSrc: "/assets/images/features/auto-apply.png",
  },
  {
    id: "resume",
    name: "Smart Resume Builder",
    title: "Smart Resume Builder",
    description:
      "An AI editor that rewrites weak bullets and scores your resume against ATS rules as you edit.",
    primaryActionText: "Build Your Resume",
    secondaryActionText: "See How It Works",
    primaryActionLink: "/resume",
    secondaryActionLink: "/features/smart-resume-builder",
    imageSrc: "/assets/images/features/resume-builder.png",
  },
  {
    id: "insider-connection",
    name: "Insider Connections",
    title: "Insider Connections",
    description:
      "Find people inside the companies you're targeting and send a personalised message asking for a referral.",
    primaryActionText: "Find Connections",
    secondaryActionText: "See How It Works",
    primaryActionLink: "/insider-connection",
    secondaryActionLink: "/features/insider-connections",
    imageSrc: "/assets/images/features/insider-connections.png",
  },
  {
    id: "board",
    name: "AI Job Match",
    title: "AI Job Match",
    description:
      "A daily feed of verified, active roles from every major portal, ranked by how well they match your skills.",
    primaryActionText: "Browse Matching Jobs",
    secondaryActionText: "See How It Works",
    primaryActionLink: "/sign-in",
    secondaryActionLink: "/features/ai-job-match",
    imageSrc: "/assets/images/features/job-match.png",
  },
  {
    id: "tracker",
    name: "JobTracker AI",
    title: "JobTracker AI",
    description:
      "Every application on one board. Drag cards between stages, and Auto Apply moves them to Applied for you.",
    primaryActionText: "Track My Jobs",
    secondaryActionText: "See How It Works",
    primaryActionLink: "/jobs/track",
    secondaryActionLink: "/features/jobtracker-ai",
    imageSrc: "/assets/images/features/job-tracker.png",
  },
  {
    id: "interview",
    name: "AI Mock Interview",
    title: "AI Mock Interview",
    description:
      "Practise with an AI interviewer that asks follow-ups and scores every answer using the STAR method.",
    primaryActionText: "Start a Mock Interview",
    secondaryActionText: "See How It Works",
    primaryActionLink: "/sign-in",
    secondaryActionLink: "/features/ai-mock-interview",
    imageSrc: "/assets/images/features/mock-interview.png",
  },
];

export const allPopularCitiesMinified = [
  "Bengaluru",
  "Hyderabad",
  "Chennai",
  "Pune",
  "Delhi",
  "Gurgaon",
  "Noida",
  "Mumbai",
  "Kolkata",
  "Ahmedabad",
];

export const allPopularJobTitles = [
  "Backend Engineer",
  "Full Stack Engineer",
  "Frontend Software Engineer",
  "React Developer",
  "UI/UX Developer",
  "Python Engineer",
  "Java Engineer",
  "C/C++ Engineer",
  "Golang Engineer",
  ".NET Engineer",
  "Embedded Software Engineer",
  "Software Architect",
  "Engineering Manager",
  "Engineering Director / VP",
  "CTO",
  "Data Analyst",
  "Data Scientist",
  "Data Engineer",
  "Power BI Developer",
  "Business/BI Analyst",
  "ETL Developer",
  "Data Warehouse Engineer",
  "Machine Learning Engineer",
  "AI Engineer",
  "Machine Learning/AI Researcher",
  "LLM Engineer",
  "MLOps Engineer",
  "AI Product Manager",
  "Data Annotation Specialist / AI Tutor",
  "Machine Learning (Computer Vision)",
  "Machine Learning (Search Systems)",
  "Machine Learning (Model Training and Inference)",
  "Machine Learning (Infrastructure)",
  "Machine Learning (Deep Learning)",
  "Machine Learning (Ads)",
  "Android Developer",
  "iOS/Swift Developer",
  "Flutter Developer",
  "Game Developer",
  "Unity Developer",
  "Unreal Engine Developer",
  "AR/VR Developer",
  "DevOps Engineer",
  "Site Reliability Engineer (SRE)",
  "Systems Engineer",
  "System Administrator",
  "Network Engineer",
  "Network Security Engineer",
  "Cloud Security Engineer",
  "IT Support Specialist",
  "Help Desk Technician / Desktop Support Technician",
  "Network Support Specialist",
  "Database Administrator",
  "Solutions Architect / Forward Deployed Engineer",
  "Cyber Security Engineer",
  "Cyber Security Analyst",
  "SoC Analyst",
  "Cloud Security Engineer",
  "Cyber Security Consultant",
  "Product Manager",
  "Technical Product Manager",
  "AI Product Manager",
  "Product Analyst",
  "Product Manager (B2B / SaaS)",
  "Product Manager (Consumer Software)",
  "Product Manager (Hardware / Robotics / IoT)",
  "Program Manager",
  "Project Manager / Program Manager",
  "Technical Project Manager",
  "Scrum Master",
  "Chief of Staff",
  "Software Testing / QA Engineer",
  "Automation Test Engineer",
  "QA Manager",
  "Quality Assurance Specialist",
  "UX Designer",
  "UI Designer",
  "Graphic Designer",
  "UX Researcher",
  "Illustrator",
  "Video Editor",
  "Animator",
  "Creative / Art Director",
  "Motion Designer",
  "3D Designer",
  "Game Designer",
  "Industrial Designer",
  "Interior Designer",
  "Landscape Designer",
  "Content Marketing / Strategy",
  "Growth Marketer",
  "Performance Marketer",
  "Product Marketing Manager",
  "Event Marketing Specialist",
  "SEO Specialist",
  "Social Media Manager",
  "Copywriter",
  "Advertising Specialist",
  "Brand Manager",
  "Public Relations Manager",
  "Community Manager",
  "Business Analyst",
  "Business Strategy Consultant",
  "Market Research Analyst",
  "Operations Consultant",
  "Change Management Consultant",
  "Financial Consultant",
  "Risk Management Consultant",
  "M&A Consultant",
  "Data Consultant",
  "IT Consultant",
  "Financial Analyst",
  "Investment Analyst / Associate",
  "Investment Manager",
  "Investment Director / VP",
  "Investment Partner",
  "Equity Analyst",
  "Quantitative Analyst / Researcher",
  "Credit Analyst",
  "Securities Trader",
  "Asset Manager",
  "Portfolio Manager",
  "Treasury Analyst",
  "Corporate Finance Analyst",
  "Financial Advisor",
  "Underwriter",
  "Actuary",
  "Business Development Manager",
  "Partnership Manager",
  "Sales Development Representative",
  "Inside Sales Representative",
  "Account Executive (SMB)",
  "Enterprise Sales Representative",
  "Field Sales Representative",
  "Channel Sales Manager",
  "Retail Sales Associate",
  "Store Manager",
  "Sales Manager",
  "Regional Sales Manager",
  "Sales Director / VP",
  "Sales Engineer",
  "Sales Operations Specialist",
  "Sales Support Specialist",
  "Medical Sales Representative",
  "Medical Device Sales Representative",
  "Insurance Sales Agent",
  "Real Estate Sales Agent",
  "Automotive Sales Consultant",
  "Human Resource Specialist",
  "Recruiter / Sourcer",
  "Recruiting Coordinator",
  "HR Business Partner",
  "Human Resource Manager / Director",
  "Payroll Specialist",
  "Data Entry Clerk",
  "Administrative Assistant",
  "Executive Assistant",
  "Office Manager",
  "Receptionist",
  "Customer Service Representative",
  "Customer Support Specialist",
  "Customer Service Manager",
  "Customer Success Manager",
  "Legal Assistant",
  "Paralegal",
  "Corporate Counsel",
  "Compliance Specialist",
  "Legal Operations Manager",
  "Intellectual Property Lawyer",
  "Immigration Lawyer",
  "Family Lawyer",
  "Criminal Lawyer",
  "Litigation Lawyer",
  "Case Manager",
  "Court Clerk",
  "Educational Administrator",
  "Academic Dean",
  "K-12 Teacher",
  "Higher Education Faculty",
  "Corporate Training & Development",
  "Healthcare Data Analyst",
  "Healthcare Data Scientist",
  "Health Product Manager",
  "EHR System Administrator",
  "Healthcare IT Specialist",
  "Clinical Research Associate",
  "Clinical Research Scientist",
  "Biostatistician",
  "Medical Writer",
  "Regulatory Affairs Specialist",
  "Biomedical Engineer",
  "Clinical Engineer",
  "Biomedical Equipment Technician",
  "Healthcare Quality Improvement Specialist",
  "Clinical Operations Manager",
  "Healthcare Compliance Manager",
  "Biologist",
  "Biochemist",
  "Chemist",
  "Pharmacologist",
  "Formulation Scientist",
  "Toxicologist",
  "DMPK Scientist",
  "Environmental Scientist",
  "Hardware Engineer",
  "Electronics Engineer",
  "Electrical Engineer",
  "FPGA Engineer",
  "PCB Engineer",
  "ASIC Engineer",
  "RF Engineer",
  "Systems Integration Engineer",
  "Embedded Software Engineer",
  "Robotics Engineer",
  "Automation Engineer",
  "Controls Engineer",
  "Electromechanical Engineer",
  "IC Design Engineer",
  "Digital IC Verification Engineer",
  "Analog IC Design Engineer",
  "Hardware Test Engineer",
  "Electrical Test Engineer",
  "Power Systems Engineer",
  "Motor Engineer",
  "Battery Engineer",
  "Mechatronics Engineer",
  "Mechanical Engineer",
  "Manufacturing Engineer",
  "Process Engineer",
  "Industrial Engineer",
  "Civil Engineer",
  "Structural Engineer",
  "Construction Project Manager",
  "Project Engineer",
  "Quality Assurance Specialist",
  "Automotive Engineer",
  "Autonomous Driving Systems Engineer",
  "Powertrain Engineer",
  "Safety Engineer",
  "Chemical Engineer",
  "Laboratory Technician",
  "EHS Engineer",
  "Architect",
  "Landscape Architect",
  "Urban Planner",
  "Leasing Consultant",
  "Property Manager",
  "Supply Chain Manager",
  "Inventory Manager",
  "Warehouse Manager",
  "Logistics Manager",
  "Distribution Center Manager",
  "Procurement Manager",
  "Facilities Manager",
  "Energy Engineer",
  "Renewable Energy Engineer",
  "Nuclear Engineer",
  "Environmental Engineer",
  "Policy Analyst",
  "Government Relations Manager",
  "Program Manager",
  "Volunteer Coordinator",
  "Fundraising Coordinator",
];

export const groupedPopularJobTitles = [
  {
    category: "Software Engineering",
    titles: [
      "Backend Engineer",
      "Full Stack Engineer",
      "Frontend Software Engineer",
      "React Developer",
      "UI/UX Developer",
      "Python Engineer",
      "Java Engineer",
      "C/C++ Engineer",
      "Golang Engineer",
      ".NET Engineer",
      "Embedded Software Engineer",
      "Software Architect",
      "Engineering Manager",
      "Engineering Director / VP",
      "CTO",
      "Android Developer",
      "iOS/Swift Developer",
      "Flutter Developer",
      "Game Developer",
      "Unity Developer",
      "Unreal Engine Developer",
      "AR/VR Developer",
    ],
  },
  {
    category: "Data & AI",
    titles: [
      "Data Analyst",
      "Data Scientist",
      "Data Engineer",
      "Power BI Developer",
      "Business/BI Analyst",
      "ETL Developer",
      "Data Warehouse Engineer",
      "Machine Learning Engineer",
      "AI Engineer",
      "Machine Learning/AI Researcher",
      "LLM Engineer",
      "MLOps Engineer",
      "AI Product Manager",
      "Data Annotation Specialist / AI Tutor",
      "Machine Learning (Computer Vision)",
      "Machine Learning (Search Systems)",
      "Machine Learning (Model Training and Inference)",
      "Machine Learning (Infrastructure)",
      "Machine Learning (Deep Learning)",
      "Machine Learning (Ads)",
    ],
  },
  {
    category: "Infrastructure & Security",
    titles: [
      "DevOps Engineer",
      "Site Reliability Engineer (SRE)",
      "Systems Engineer",
      "System Administrator",
      "Network Engineer",
      "Network Security Engineer",
      "Cloud Security Engineer",
      "IT Support Specialist",
      "Help Desk Technician / Desktop Support Technician",
      "Network Support Specialist",
      "Database Administrator",
      "Solutions Architect / Forward Deployed Engineer",
      "Cyber Security Engineer",
      "Cyber Security Analyst",
      "SoC Analyst",
      "Cyber Security Consultant",
    ],
  },
  {
    category: "Product & Project Management",
    titles: [
      "Product Manager",
      "Technical Product Manager",
      "Product Analyst",
      "Product Manager (B2B / SaaS)",
      "Product Manager (Consumer Software)",
      "Product Manager (Hardware / Robotics / IoT)",
      "Program Manager",
      "Project Manager / Program Manager",
      "Technical Project Manager",
      "Scrum Master",
      "Chief of Staff",
    ],
  },
  {
    category: "Quality Assurance",
    titles: [
      "Software Testing / QA Engineer",
      "Automation Test Engineer",
      "QA Manager",
      "Quality Assurance Specialist",
    ],
  },
  {
    category: "Design & Creative",
    titles: [
      "UX Designer",
      "UI Designer",
      "Graphic Designer",
      "UX Researcher",
      "Illustrator",
      "Video Editor",
      "Animator",
      "Creative / Art Director",
      "Motion Designer",
      "3D Designer",
      "Game Designer",
      "Industrial Designer",
      "Interior Designer",
      "Landscape Designer",
    ],
  },
  {
    category: "Marketing & Communications",
    titles: [
      "Content Marketing / Strategy",
      "Growth Marketer",
      "Performance Marketer",
      "Product Marketing Manager",
      "Event Marketing Specialist",
      "SEO Specialist",
      "Social Media Manager",
      "Copywriter",
      "Advertising Specialist",
      "Brand Manager",
      "Public Relations Manager",
      "Community Manager",
    ],
  },
  {
    category: "Business & Finance",
    titles: [
      "Business Analyst",
      "Business Strategy Consultant",
      "Market Research Analyst",
      "Operations Consultant",
      "Change Management Consultant",
      "Financial Consultant",
      "Risk Management Consultant",
      "M&A Consultant",
      "Data Consultant",
      "IT Consultant",
      "Financial Analyst",
      "Investment Analyst / Associate",
      "Investment Manager",
      "Investment Director / VP",
      "Investment Partner",
      "Equity Analyst",
      "Quantitative Analyst / Researcher",
      "Credit Analyst",
      "Securities Trader",
      "Asset Manager",
      "Portfolio Manager",
      "Treasury Analyst",
      "Corporate Finance Analyst",
      "Financial Advisor",
      "Underwriter",
      "Actuary",
    ],
  },
  {
    category: "Sales",
    titles: [
      "Business Development Manager",
      "Partnership Manager",
      "Sales Development Representative",
      "Inside Sales Representative",
      "Account Executive (SMB)",
      "Enterprise Sales Representative",
      "Field Sales Representative",
      "Channel Sales Manager",
      "Retail Sales Associate",
      "Store Manager",
      "Sales Manager",
      "Regional Sales Manager",
      "Sales Director / VP",
      "Sales Engineer",
      "Sales Operations Specialist",
      "Sales Support Specialist",
      "Medical Sales Representative",
      "Medical Device Sales Representative",
      "Insurance Sales Agent",
      "Real Estate Sales Agent",
      "Automotive Sales Consultant",
    ],
  },
  {
    category: "HR & Administration",
    titles: [
      "Human Resource Specialist",
      "Recruiter / Sourcer",
      "Recruiting Coordinator",
      "HR Business Partner",
      "Human Resource Manager / Director",
      "Payroll Specialist",
      "Data Entry Clerk",
      "Administrative Assistant",
      "Executive Assistant",
      "Office Manager",
      "Receptionist",
    ],
  },
  {
    category: "Customer Success & Support",
    titles: [
      "Customer Service Representative",
      "Customer Support Specialist",
      "Customer Service Manager",
      "Customer Success Manager",
    ],
  },
  {
    category: "Legal",
    titles: [
      "Legal Assistant",
      "Paralegal",
      "Corporate Counsel",
      "Compliance Specialist",
      "Legal Operations Manager",
      "Intellectual Property Lawyer",
      "Immigration Lawyer",
      "Family Lawyer",
      "Criminal Lawyer",
      "Litigation Lawyer",
      "Case Manager",
      "Court Clerk",
    ],
  },
  {
    category: "Education",
    titles: [
      "Educational Administrator",
      "Academic Dean",
      "K-12 Teacher",
      "Higher Education Faculty",
      "Corporate Training & Development",
    ],
  },
  {
    category: "Healthcare & Science",
    titles: [
      "Healthcare Data Analyst",
      "Healthcare Data Scientist",
      "Health Product Manager",
      "EHR System Administrator",
      "Healthcare IT Specialist",
      "Clinical Research Associate",
      "Clinical Research Scientist",
      "Biostatistician",
      "Medical Writer",
      "Regulatory Affairs Specialist",
      "Biomedical Engineer",
      "Clinical Engineer",
      "Biomedical Equipment Technician",
      "Healthcare Quality Improvement Specialist",
      "Clinical Operations Manager",
      "Healthcare Compliance Manager",
      "Biologist",
      "Biochemist",
      "Chemist",
      "Pharmacologist",
      "Formulation Scientist",
      "Toxicologist",
      "DMPK Scientist",
      "Environmental Scientist",
    ],
  },
  {
    category: "Hardware & Engineering",
    titles: [
      "Hardware Engineer",
      "Electronics Engineer",
      "Electrical Engineer",
      "FPGA Engineer",
      "PCB Engineer",
      "ASIC Engineer",
      "RF Engineer",
      "Systems Integration Engineer",
      "Robotics Engineer",
      "Automation Engineer",
      "Controls Engineer",
      "Electromechanical Engineer",
      "IC Design Engineer",
      "Digital IC Verification Engineer",
      "Analog IC Design Engineer",
      "Hardware Test Engineer",
      "Electrical Test Engineer",
      "Power Systems Engineer",
      "Motor Engineer",
      "Battery Engineer",
      "Mechatronics Engineer",
      "Mechanical Engineer",
      "Manufacturing Engineer",
      "Process Engineer",
      "Industrial Engineer",
      "Civil Engineer",
      "Structural Engineer",
      "Construction Project Manager",
      "Project Engineer",
      "Automotive Engineer",
      "Autonomous Driving Systems Engineer",
      "Powertrain Engineer",
      "Safety Engineer",
      "Chemical Engineer",
      "Laboratory Technician",
      "EHS Engineer",
    ],
  },
  {
    category: "Other Professional Roles",
    titles: [
      "Architect",
      "Landscape Architect",
      "Urban Planner",
      "Leasing Consultant",
      "Property Manager",
      "Supply Chain Manager",
      "Inventory Manager",
      "Warehouse Manager",
      "Logistics Manager",
      "Distribution Center Manager",
      "Procurement Manager",
      "Facilities Manager",
      "Energy Engineer",
      "Renewable Energy Engineer",
      "Nuclear Engineer",
      "Environmental Engineer",
      "Policy Analyst",
      "Government Relations Manager",
      "Volunteer Coordinator",
      "Fundraising Coordinator",
    ],
  },
];

export const convertLinkedinJobDetailToSearchUrl = (job: JobSearchResultV2) => {
  if (!job.applyUrl) return null;
  if (job.platformName !== "LinkedIn") return null;

  // Return the /jobs/view/<id>/ URL directly. The scraper already stores
  // applyUrl in this format. The individual job page triggers the cleaner
  // applySingleLinkedInJob() path in the extension (vs the search-page SPA
  // path), avoids the search-page timing race that throws "website not
  // supported", and has a simpler DOM for the Easy Apply button finder.
  // The backend's cleanJobUrl adds ?mmjAutoApply=true automatically.
  return job.applyUrl;
};

export const chromeExtensionIds = [
  "kknbggpbopgndnbpdffpklddolhmfpke", // Webstore ID
  "jbohfdjoafaklacappmjageioahohjfh", // Prod ID
  "jfmffgbmfkfgagbfoeigbmcokigbapga", // Local ID
];

export const getUserPlanName = (plan: string) => {
  switch (plan) {
    case "USER":
      return "Trial (Free)";
    case "PREMIUM":
      return "Silver";
    case "PREMIUM_PRO":
      return "Gold";
    case "CAREER_COACH":
      return "Career Coach";
    case "ADMIN":
      return "Admin";
    case "MODERATOR":
      return "Moderator";
    case "AUTHOR":
      return "Author";
    case "no_plan":
      return "No Plan";
    default:
      return "Unknown";
  }
};
